import type { ConnectionOptions, MsgHdrs, NatsConnection, Subscription } from "nats.ws";
import { JSONCodec, headers, jwtAuthenticator } from "nats.ws";
import { connect } from "nats.ws";
import type { Result } from "../models/result";
// import { createContext } from "react";
import { signal } from "@preact/signals-react";
import { generateGuid, generateUID, parseAuthCookies, getApiUrl } from "./utils";
import { LogTag, log } from "./logger";
import { getOidc } from "./auth";
import { performPostLogin } from "./auth";
import { isNative } from "./native";
import { decodeJwt } from "oidc-spa/tools/decodeJwt";
import { getTokenProvider } from "./tokenProvider";

export enum Status {
  Online,
  Offline,
  Connecting,
  NotAvailable,
}

const serverUrl = import.meta.env.VITE_NATS_SERVER_URL;
const useJwtAuth = import.meta.env.VITE_NATS_AUTH_JWT == "true";

const jc = JSONCodec();
let nc: NatsConnection | null = null;

const msgHeaders = headers();

let connectPromise: Promise<NatsConnection | null> | null = null;
let getCookiesPromise: Promise<{ jwt: string; seed: string; token: string }> | null = null;

export const status = signal(Status.Offline);

// Track if HTTP protocol is forced - initialize from localStorage
const getInitialHttpForced = (): boolean => {
  try {
    const stored = localStorage.getItem('NG_ForceHttp');
    return stored === 'true';
  } catch {
    return false;
  }
};

export const isHttpForced = signal(getInitialHttpForced());

/**
 * Force the client to use HTTP protocol instead of NATS
 * @param force - true to force HTTP mode, false to allow normal NATS operation
 */
export function forceHttpProtocol(force: boolean): void {
  isHttpForced.value = force;

  if (force) {
    // Set status to NotAvailable to trigger HTTP mode
    status.value = Status.NotAvailable;

    // Disconnect any existing connection
    if (nc) {
      nc.drain();
      nc.close();
      nc = null;
      connectPromise = null;
    }

    log.info(tag, "HTTP protocol forced - all requests will use HTTP endpoints");
  } else {
    // Set status to Offline to allow normal reconnection
    status.value = Status.Offline;
    log.info(tag, "HTTP protocol force disabled - normal messaging operation resumed");
  }
}

const tag: LogTag = "client";

// Helper function to check if a NATS subject represents a read-only operation
function isReadOnlyOperation(subject: string): boolean {
  const lowerCaseSubject = subject.toLowerCase();
  const words = lowerCaseSubject.split(".");
  return words.includes("query") || words.includes("get");
}

export const getCookiesWithRetry = (retryCount = 100): Promise<{ jwt: string | null; seed: string | null; token: string }> => {
  // If a custom token provider is set, use it instead
  const tokenProvider = getTokenProvider();
  if (tokenProvider) {
    return tokenProvider.getTokenData();
  }

  // For NATS, we also need jwt and seed, so we still need to use cookies directly
  // The token provider only provides the main token
  // If there's already a promise in progress, return it
  if (getCookiesPromise) {
    return getCookiesPromise;
  }

  getCookiesPromise = (async () => {
    try {
      const cookies = parseAuthCookies();
      const jwt = cookies["Api.NatsJwt"];
      const seed = cookies["Api.NatsSeed"];
      const token = cookies["Api.Token"];

      if (jwt && seed && token) {
        // log.info(tag, "Retrieved cookies successfully");
        return { jwt, seed, token };
      }

      if (retryCount > 0) {
        log.info(tag, `Retrying to get auth tokens, attempts left: ${retryCount}`, jwt, seed, token);

        // If not native and cookies are missing, try to refresh auth
        if (!isNative()) {
          try {
            // retry to refresh the cookies
            const auth = await getOidc();
            if (auth.isUserLoggedIn) {
              const tokens = auth.getTokens();
              const token = decodeJwt(tokens.accessToken);
              await performPostLogin(auth, tokens, token, true);

              // After refresh, try to get cookies again
              await new Promise(resolve => setTimeout(resolve, 100));
              const refreshedCookies = parseAuthCookies();
              const refreshedJwt = refreshedCookies["Api.NatsJwt"];
              const refreshedSeed = refreshedCookies["Api.NatsSeed"];
              const refreshedToken = refreshedCookies["Api.Token"];

              if (refreshedJwt && refreshedSeed && refreshedToken) {
                return { jwt: refreshedJwt, seed: refreshedSeed, token: refreshedToken };
              }
            }
          } catch (authError) {
            log.error(tag, "Failed to refresh auth", authError);
          }
        }

        // If auth refresh failed or we're in native mode, retry with delay
        await new Promise(resolve => setTimeout(resolve, 50));
        return await getCookiesWithRetry(retryCount - 1);
      } else {
        log.error(tag, "Failed to retrieve auth tokens after retries", jwt, seed, token);
        throw new Error("Unauthorized: Failed to retrieve authentication tokens");
      }
    } catch (error) {
      log.error(tag, "Error in token retrieval", error);
      throw error;
    }
  })();

  // Clear the promise when it completes (success or failure)
  getCookiesPromise.finally(() => {
    getCookiesPromise = null;
  });

  return getCookiesPromise;
};

export function ensureConnected(): Promise<NatsConnection | null> {
  // If HTTP is forced, always return null to trigger HTTP mode
  if (isHttpForced.value) return Promise.resolve(null);

  if (status.value == Status.NotAvailable) return Promise.resolve(null);
  if (status.value == Status.Online) return Promise.resolve(nc);
  if (status.value == Status.Connecting && connectPromise) return connectPromise;

  const id = generateUID();
  status.value = Status.Connecting;

  const opts: ConnectionOptions = { servers: serverUrl };

  log.info(tag, "Called ensureConnected", tag, "id:", id, "status:", status.value, "nc:", nc);

  connectPromise = getCookiesWithRetry()
    .then(({ jwt, seed, token }) => {
      msgHeaders.set("Token", token);

      // Handle case where jwt/seed might be null (e.g., from embed token provider)
      if (jwt && seed) {
        opts.authenticator = jwtAuthenticator(jwt, new TextEncoder().encode(seed));
      }

      setBranchHeader();

      log.info(tag, `Connecting to: ${opts.servers}, jwt: ${useJwtAuth}`, id);
      return connect(opts)
        .then((conn) => {
          nc = conn;
          status.value = Status.Online;
          log.info(tag, `Connected to: ${nc.getServer()}`, id);

          (async () => {
            for await (const s of nc.status()) {
              switch (s.type) {
                case "reconnect":
                  status.value = Status.Online;
                  break;
                case "disconnect":
                  status.value = Status.Offline;
                  break;
                case "reconnecting":
                  status.value = Status.Connecting;
                  break;
                case "update":
                default:
                  // Do nothing
                  break;
              }
              log.info(tag, `Status ${s.type}: ${s.data}`, s, status.value);
            }
          })();

          nc.closed().then((e) => {
            log.info(tag, "Connection closed", e);
            status.value = Status.Offline;
          });

          return nc;
        })
        .catch((e) => {
          log.error(tag, `Error connecting: ${e}`, id);
          status.value = Status.NotAvailable;
          return null;
        });
    })
    .catch((error) => {
      log.error(tag, `Failed to retrieve auth tokens: ${error}`);
      status.value = Status.NotAvailable;
      return Promise.reject(error);
    });

  return connectPromise;
}

function setBranchHeader() {
  const branch = parseAuthCookies()["Api.Branch"];
  if (branch) {
    msgHeaders.set("Branch", branch);
  }
}

export async function subscribe(subject: string, action: (message: unknown) => void): Promise<Subscription | null> {
  const nc = await ensureConnected();

  // If HTTP is forced or NATS is not available, use SSE
  if (isHttpForced.value || !nc) {
    return subscribeSSE(subject, action);
  }

  const sub = nc.subscribe(subject);

  (async () => {
    for await (const m of sub) {
      const message = jc.decode(m.data);
      log.info(tag, `[${sub.getProcessed()}]: ${m.subject} - ${message}`);
      action(message);
    }
  })();

  return sub;
}

async function subscribeSSE(subject: string, action: (message: unknown) => void): Promise<Subscription | null> {
  try {
    const baseHeaders = await getHeaders();

    // Build query parameters from headers
    const params = new URLSearchParams();

    // Add headers as query parameters (skip Token since Cloudflare Workers handles Authorization from cookies)
    baseHeaders.keys().forEach((key) => {
      const value = baseHeaders.get(key);
      if (value && key !== "Token") {
        params.append(key, value);
      }
    });

    // Create SSE URL - Cloudflare Workers will add Authorization header from cookies automatically
    const url = `${getApiUrl()}/sse/${subject}?${params.toString()}`;

    // EventSource will send cookies which Cloudflare Workers will convert to Authorization header
    const eventSource = new EventSource(url, {
      withCredentials: true
    });

    eventSource.onopen = () => {
      log.info(tag, `SSE connection opened for subject: ${subject}`);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        log.info(tag, `SSE received: ${subject} - ${data}`);
        action(data);
      } catch (err: unknown) {
        log.error(tag, `SSE parsing error: ${err}`, event.data);
      }
    };

    eventSource.addEventListener('data', (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        log.info(tag, `SSE data event: ${subject} - ${data}`);
        action(data);
      } catch (err: unknown) {
        log.error(tag, `SSE data parsing error: ${err}`, event);
      }
    });

    eventSource.addEventListener('error', (event) => {
      log.error(tag, `SSE error event: ${subject}`, event);
    });

    eventSource.addEventListener('connected', (event) => {
      log.info(tag, `SSE connected event: ${subject}`, event);
    });

    eventSource.onerror = (error) => {
      log.error(tag, `SSE connection error for subject: ${subject}`, error);
    };

    // Return a subscription-like object that can be unsubscribed
    return {
      unsubscribe: () => {
        eventSource.close();
        log.info(tag, `SSE connection closed for subject: ${subject}`);
      },
      getProcessed: () => 0, // SSE doesn't track processed messages the same way
      getSubject: () => subject
    } as Subscription;

  } catch (error) {
    log.error(tag, `Failed to establish SSE connection for subject: ${subject}`, error);
    return null;
  }
}

export async function syncKvJetstream(name, pageId: string, source: string, action: (message: unknown) => void) {
  const nc = await ensureConnected();
  if (!nc) return;
  const js = await nc.jetstream();
  const kv = await js.views.kv(name);
  if (!kv) {
    console.error(`Nats key-value with name ${name} not available`);
    return;
  }
  const iter = await kv.watch({
    key: `${pageId}.${source}`,
  });

  let cancelled = false;

  (async () => {
    for await (const m of iter) {
      if (cancelled) break;
      const message = jc.decode(m.value);
      log.info(tag, `[${iter.getProcessed()}]: ${m.subject} - ${message}`);
      action(message);
    }
  })();

  return () => {
    cancelled = true;
    iter.stop();
  };
}

export async function putKvJetstream(name, pageId, source, value) {
  const nc = await ensureConnected();
  if (!nc) return;
  const js = await nc.jetstream();
  const kv = await js.views.kv(name);
  if (!kv) {
    console.error(`Nats key-value with name ${name} not available`);
    return;
  }
  await kv.put(`${pageId}.${source}`, jc.encode(value));
}

export async function purgeKvJetStream(name) {
  const nc = await ensureConnected();
  if (!nc) return;
  const js = await nc.jetstream();
  const kv = await js.views.kv(name);
  const keys = await kv.keys();

  for await (const key of keys) {
    await kv.purge(key);
    log.info(tag, "Purging Jetstream key : ", key);
  }
}

export async function checkKeyExist(name, pageId, source, callback) {
  const nc = await ensureConnected();
  if (!nc) return;
  const js = await nc.jetstream();
  const kv = await js.views.kv(name);
  if (!kv) {
    console.error(`Nats key-value with name ${name} not available`);
    return;
  }
  const keyExist = (await kv.get(`${pageId}.${source}`)) !== null;
  callback(keyExist);
  // return (await kv.get(`${pageId}.${source}`)) !== null;
}

export function disconnect() {
  log.info(tag, "Disconnecting");
  nc?.drain();
  nc?.close();
  status.value = Status.Offline;
}

async function getHeaders() {
  const { token } = await getCookiesWithRetry();
  msgHeaders.set("Token", token);
  setBranchHeader();
  return msgHeaders;
}

function toHttpHeaders(natsHeaders: MsgHdrs, type = "application/json") {
  const httpHeaders = {};

  natsHeaders.keys().forEach((key) => {
    httpHeaders[key] = natsHeaders.get(key);
  });

  httpHeaders["Content-Type"] = type;

  // Add Authorization header if Token is present
  const token = natsHeaders.get("Token");
  if (token) {
    httpHeaders["Authorization"] = `Bearer ${token}`;
  }

  return httpHeaders;
}

function addCustomHeaders(baseHeaders: MsgHdrs, headers: object) {
  for (const key in headers) {
    if (Object.prototype.hasOwnProperty.call(headers, key)) {
      baseHeaders.set(key, headers[key]);
    }
  }

  return baseHeaders;
}

export async function publish(subject, data, headers: object = {}) {
  const nc = await ensureConnected();

  const baseHeaders = await getHeaders();
  const customHeaders = addCustomHeaders(baseHeaders, headers);

  if (isHttpForced.value || status.value == Status.NotAvailable) {
    try {
      const resp = await fetch(`${getApiUrl()}/proxy/${subject}`, {
        method: "POST",
        headers: toHttpHeaders(customHeaders),
        body: JSON.stringify(data),
      });
      if (!resp.ok) {
        log.error(tag, `HTTP Proxy publish failed: ${resp.statusText}`, subject, data, customHeaders);
        return { success: false, reasons: [{ message: resp.statusText, status: resp.status }] };
      }
      log.info(tag, `HTTP Proxy publish successful: ${subject}`, data);
      return { success: true };
    } catch (err: any) {
      log.error(tag, `HTTP Proxy publish error: ${err}`, subject, data, customHeaders);
      return { success: false, reasons: [err.message] };
    }
  }

  if (!nc) return { success: false, reasons: [{ message: "Not connected" }] };

  try {
    nc.publish(subject, jc.encode(data), {
      headers: customHeaders,
    });
    log.info(tag, `Publish successful: ${subject}`, data);
    return { success: true };
  } catch (err: any) {
    log.groupError(tag, `Error: ${err}. Publish to ${subject} failed`);
    log.info(tag, "data:", data);
    log.info(tag, "headers:", {
      "NG-SessionId": customHeaders.get("NG-SessionId"),
      "NG-RequestId": customHeaders.get("NG-RequestId"),
    });
    log.groupEnd();
    return { success: false, reasons: [err.message] };
  }
}

export async function request<T>(
  subject: string,
  data: object,
  headers: object = {},
  timeout: number,
  retries = 3,
  retryDelay = 1000,
  abortSignal?: AbortSignal
): Promise<Result<T>> {
  const nc = await ensureConnected();

  // TODO: Remove later
  // timeout = 300; // 5min

  const baseHeaders = await getHeaders();
  const customHeaders = addCustomHeaders(baseHeaders, headers);
  const isReadOnly = isReadOnlyOperation(subject);
  const startTimeout = timeout ?? (isReadOnly ? 15 : 300)

  let attempt = 0;
  const requestId = customHeaders.get("NG-RequestId") || generateUID();
  const activeRequests = new Map<string, Promise<any>>();

  while (attempt < retries) {
    const attemptId = `${requestId}-${attempt}`;
    // Increase timeout for each retry (exponential backoff)
    const currentTimeout = startTimeout * Math.pow(2, attempt);

    try {
      let requestPromise: Promise<any>;

      if (isHttpForced.value || status.value == Status.NotAvailable) {
        requestPromise = (async () => {
          const controller = new AbortController();

          // Handle external abort signal
          if (abortSignal) {
            if (abortSignal.aborted) {
              throw new Error("Request was aborted before starting");
            }
            abortSignal.addEventListener('abort', () => {
              controller.abort();
            });
          }

          // Create a timeout promise that rejects after currentTimeout
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => {
              controller.abort();
              reject(new Error(`Request timeout after ${currentTimeout}s`));
            }, currentTimeout * 1000);
          });

          // Race the actual request against the timeout
          const fetchPromise = fetch(`${getApiUrl()}/proxy/${subject}`, {
            method: "POST",
            headers: toHttpHeaders(customHeaders),
            body: JSON.stringify(data),
            signal: controller.signal,
          })
            .then(async (resp) => {
              if (!resp.ok) {
                throw new Error(`HTTP Proxy publish failed: ${resp.statusText}`);
              }
              return await resp.json();
            });

          // Race fetch against timeout - this will throw if timeout occurs
          return await Promise.race([fetchPromise, timeoutPromise]);
        })();
      } else {
        if (!nc) throw new Error("Not connected");

        requestPromise = (async () => {
          // Check if already aborted
          if (abortSignal?.aborted) {
            throw new Error("Request was aborted before starting");
          }

          const natsRequestPromise = nc.request(subject, jc.encode(data), {
            timeout: currentTimeout * 1000,
            headers: customHeaders,
          }).then((msg) => {
            if (msg === undefined) {
              throw new Error("No response");
            }
            return jc.decode(msg.data) as Result<T>;
          });

          // If abort signal is provided, race the request against abort
          if (abortSignal) {
            const abortPromise = new Promise<never>((_, reject) => {
              const onAbort = () => {
                reject(new Error("Request was aborted"));
              };

              if (abortSignal.aborted) {
                reject(new Error("Request was aborted"));
                return;
              }

              abortSignal.addEventListener('abort', onAbort);

              // Clean up listener when request completes
              natsRequestPromise.finally(() => {
                abortSignal.removeEventListener('abort', onAbort);
              });
            });

            return await Promise.race([natsRequestPromise, abortPromise]);
          } else {
            return await natsRequestPromise;
          }
        })();
      }

      // Store the active request
      activeRequests.set(attemptId, requestPromise);

      // Race all active requests - first one to succeed wins
      const result = await Promise.race(Array.from(activeRequests.values()));

      // Clear all active requests since we got a result
      activeRequests.clear();

      return result;
    } catch (err: unknown) {
      // Remove this failed attempt from active requests
      activeRequests.delete(attemptId);

      // Check if this was an abort error - don't retry abort errors
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.includes("aborted")) {
        activeRequests.clear();
        return { success: false, reasons: [{ message: errorMessage }] };
      }

      attempt++;
      // add log to print current request and attempt and error
      log.groupError(tag, `Request to ${subject} failed: ${err}. Attempt: ${attempt} of ${retries}`);
      log.info(tag, "data:", data);
      log.info(tag, "headers:", {
        "NG-SessionId": customHeaders.get("NG-SessionId"),
        "NG-RequestId": customHeaders.get("NG-RequestId"),
      });
      log.info(tag, "timeout:", currentTimeout);
      log.groupEnd();
      const is503 = err instanceof Error && (err as any).code === "503";
      /**
       * Determines whether an operation should be retried based on the current attempt count,
       * the maximum number of retries, the read-only status, and the error code.
       *
       * Logical Matrix for `shouldRetry`:
       * | attempt < retries | isReadOnly | err.code === "503" | shouldRetry |
       * |-------------------|------------|--------------------|-------------|
       * | true              | true       | false              | true        |
       * | true              | false      | true               | true        |
       * | true              | false      | false              | false       |
       * | false             | true       | false              | false       |
       * | false             | false      | true               | false       |
       * | false             | false      | false              | false       |
       *
       * @param attempt - The current attempt count.
       * @param retries - The maximum number of retries allowed.
       * @param isReadOnly - Indicates whether the operation is read-only.
       * @param err - The error object containing the error code.
       * @returns `true` if the operation should be retried; otherwise, `false`.
       */
      const shouldRetry = (attempt < retries) && (isReadOnly || is503);
      // const shouldRetry = (attempt < retries) && (err instanceof Error && (err as any).code === "503");

      if (shouldRetry) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      } else {
        // Wait a bit more for any remaining active requests before giving up
        if (activeRequests.size > 0) {
          try {
            const result = await Promise.race([
              Promise.race(Array.from(activeRequests.values())),
              new Promise((_, reject) => setTimeout(() => reject(new Error("Final timeout")), retryDelay))
            ]);
            activeRequests.clear();
            return result;
          } catch {
            // All requests failed
          }
        }
        activeRequests.clear();
        return { success: false, reasons: [{ message: errorMessage }] };
      }
    }
  }

  return { success: false, reasons: [{ message: "Max retries reached" }] };
}

async function getObjectStore(bucket) {
  if (nc == null || status.value != Status.Online) return { success: false, reasons: [{ message: "Not connected" }] };

  const js = nc.jetstream();
  const os = await js.views.os(bucket);

  return { success: true, data: os };
}

function getId() {
  return nc?.info?.client_id ?? generateGuid();
}

async function streamToBlob(stream, mimeType = "application/octet-stream") {
  const reader = stream.getReader();
  const chunks = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break; // Exit the loop when stream is finished
    chunks.push(value); // Collect each chunk
  }

  // Combine the chunks into a single Blob
  return new Blob(chunks, { type: mimeType });
}

export async function objPut(
  bucket: string,
  name: string,
  description: string,
  fileSize: number,
  stream: ReadableStream<Uint8Array>
) {
  // if (status.value == Status.NotAvailable) {
  const baseHeaders = await getHeaders();
  const customHeaders = addCustomHeaders(baseHeaders, headers);
  const blob = await streamToBlob(stream);
  const resp = await fetch(`${getApiUrl()}/files/nats/${bucket}/${name}`, {
    method: "PUT",
    headers: toHttpHeaders(customHeaders, blob.type),
    body: blob,
  });
  return await resp.json();
  // }
  // const res = await getObjectStore(bucket);
  // if (!res.success || res.data == null) return res;
  // const os = res.data;

  // const osStatus = await os.status();
  // const curSize = osStatus.size;
  // const maxBytes = osStatus.streamInfo.config.max_bytes;
  // log.info(
  //   tag,
  //   "~~status: ",
  //   osStatus,
  //   "size: ",
  //   osStatus.size,
  //   "max:",
  //   maxBytes,
  //   "curSize + fileSize: ",
  //   curSize + fileSize
  // );
  // // maxBytes = -1 means no limit
  // if (maxBytes > 0 && curSize + fileSize >= maxBytes) {
  //   return {
  //     success: false,
  //     reasons: [{ message: "Max store size exceeded" }],
  //   };
  // }

  // try {
  //   const info = await os.put({ name: name, description }, stream, {
  //     timeout: 5 * 60 * 1000, // 5 minutes
  //   });
  //   return { success: true, data: info, reasons: [] };
  // } catch (e) {
  //   return { success: false, reasons: [{ message: "Could not upload file" }] };
  // }
}

export async function objGet(bucket: string, name: string) {
  // if (status.value == Status.NotAvailable) {
  const baseHeaders = await getHeaders();
  const customHeaders = addCustomHeaders(baseHeaders, headers);
  const resp = await fetch(`${getApiUrl()}/files/nats/${bucket}/${name}`, {
    method: "GET",
    headers: toHttpHeaders(customHeaders),
  });
  if (!resp.ok) {
    return { success: false, reasons: [{ message: "Failed to fetch file", status: resp.status }] };
  }
  const blob = await resp.blob();
  log.info(tag, "objGet", blob);
  return { success: true, data: blob };
  // }

  // const res = await getObjectStore(bucket);
  // if (!res.success || res.data == null) return res;
  // const os = res.data;

  // const data = await os.getBlob(name);
  // if (data == null) return { success: false, reasons: [{ message: "Failed to get file" }] };
  // return { success: true, data: new Blob([data.buffer]) };
}

export async function objDelete(bucket: string, name: string) {
  // if (status.value == Status.NotAvailable) {
  const baseHeaders = await getHeaders();
  const customHeaders = addCustomHeaders(baseHeaders, headers);
  const resp = await fetch(`${getApiUrl()}/files/nats/${bucket}/${name}`, {
    method: "DELETE",
    headers: toHttpHeaders(customHeaders),
  });
  return await resp.json();
  // }
  // const res = await getObjectStore(bucket);
  // if (!res.success || res.data == null) return res;
  // const os = res.data;

  // const result = await os.delete(name);
  // return { success: result.success, reasons: [] };
}

export const client = {
  connect: ensureConnected,
  request: request,
  publish: publish,
  disconnect: disconnect,
  subscribe: subscribe,
  putKvJetstream: putKvJetstream,
  checkKeyExist: checkKeyExist,
  syncKvJetstream: syncKvJetstream,
  objPut: objPut,
  objGet: objGet,
  objDelete: objDelete,
  getId: getId,
  purgeKvJetStream: purgeKvJetStream,
  forceHttpProtocol: forceHttpProtocol,
};